import React from 'react';
import { motion } from 'framer-motion';
import { EarIcon, CrosshairIcon, CheckCircle2Icon, BrainIcon, BoxIcon } from 'lucide-react';
export function LeadsArchSection() {
  const steps = [{
    letter: 'L',
    title: 'Listening to\nResonate',
    icon: EarIcon,
    text: 'Deep listening that builds trust. When people feel heard, they stop performing alignment and start telling you the truth about what is actually happening.'
  }, {
    letter: 'E',
    title: 'Engage with\nPowerful Questions',
    icon: BoxIcon,
    text: 'Questions that surface the real obstacles killing execution. Better questions produce better ownership, because people act on insight they discovered themselves.'
  }, {
    letter: 'A',
    title: 'Articulate the\nGreatest Need',
    icon: CrosshairIcon,
    text: 'Naming the real challenge, not the symptom. When the greatest need is clear, strategy simplifies and the next critical moves become obvious.'
  }, {
    letter: 'D',
    title: 'Decide\nActions',
    icon: CheckCircle2Icon,
    text: 'One clear action with real ownership and built-in accountability. Not a list of ten things. One step that creates traction you can measure.'
  }, {
    letter: 'S',
    title: 'Self-Awareness\nCheck',
    icon: BrainIcon,
    text: 'Reading yourself and others accurately under pressure. Without this, leaders keep creating the same misalignment without realizing they are the source.'
  }];
  // Positions pulled inward so labels fit within the wider viewBox
  const circlePositions = [{
    cx: 350,
    cy: 62
  }, {
    cx: 570,
    cy: 210
  }, {
    cx: 490,
    cy: 430
  }, {
    cx: 210,
    cy: 430
  }, {
    cx: 130,
    cy: 210
  } // S - left
  ];
  const arrowPaths = ['M 388 80 C 440 90, 510 140, 550 185', 'M 580 250 C 572 320, 545 385, 510 410', 'M 455 450 C 390 470, 300 470, 245 450', 'M 188 410 C 155 375, 135 310, 128 250', 'M 148 180 C 175 120, 240 75, 312 60'];
  return <section className="py-16 md:py-24 bg-warm-gray" data-id="element-167">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12" data-id="element-168">
        {/* Two-column layout: Text left, Diagram right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20 md:mb-28" data-id="element-169">
          {/* Left: Title and intro text */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.7,
          ease: 'easeOut'
        }} data-id="element-170">
            <div className="flex items-center gap-4 mb-6" data-id="element-171">
              <div className="w-12 h-[1.5px] bg-gold" data-id="element-172" />
              <span className="font-heading font-bold text-[17px] text-gold uppercase tracking-[0.1em]" data-id="element-173">
                The Framework
              </span>
            </div>
            <h2 className="text-[28px] md:text-[36px] mb-8 tracking-[-0.02em]" data-id="element-174">
              The L.E.A.D.S. Coaching Arch
            </h2>
            <p className="text-[16px] leading-[1.75] mb-8" data-id="element-175">
              I created the L.E.A.D.S. Coaching Arch to solve exactly this. The
              gap between strategy and execution is not a planning problem. It
              is a leadership conversation problem.
            </p>
            <p className="text-[16px] leading-[1.75]" data-id="element-176">
              When leaders learn to listen differently, ask better questions,
              name what really matters, and move people toward clear action with
              real self-awareness, the gap closes.
            </p>
          </motion.div>

          {/* Right: Circular Diagram - Desktop */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="hidden lg:block relative w-full max-w-[580px] mx-auto" data-id="element-177">
            <svg viewBox="0 0 700 500" className="w-full h-auto" overflow="visible" data-id="element-178">
              <defs data-id="element-179">
                <marker id="arrowhead" markerWidth="10" markerHeight="8" refX="8" refY="4" orient="auto" data-id="element-180">
                  <path d="M 0 0 L 10 4 L 0 8 Z" fill="#1F3A5F" data-id="element-181" />
                </marker>
              </defs>

              {/* Connecting arrows */}
              {arrowPaths.map((d, i) => <motion.path key={i} d={d} fill="none" stroke="#1F3A5F" strokeWidth="3" strokeLinecap="round" markerEnd="url(#arrowhead)" initial={{
              pathLength: 0,
              opacity: 0
            }} whileInView={{
              pathLength: 1,
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.3 + i * 0.15,
              ease: 'easeOut'
            }} data-id="element-182" />)}

              {/* Center text - larger and bolder */}
              <text x="350" y="245" textAnchor="middle" fill="#1F3A5F" fontSize="28" fontWeight="700" letterSpacing="-0.5" fontFamily="Montserrat, sans-serif" data-id="element-183">
                L.E.A.D.S.™
              </text>
              <text x="350" y="278" textAnchor="middle" fill="#1F3A5F" fontSize="19" fontWeight="700" fontFamily="Montserrat, sans-serif" data-id="element-184">
                Coaching Arch
              </text>

              {/* Step circles and labels */}
              {circlePositions.map((pos, i) => {
              const step = steps[i];
              const labelLines = step.title.split('\n');
              const labelOffsets = [{
                dx: 0,
                dy: -58,
                anchor: 'middle' as const
              }, {
                dx: 50,
                dy: 5,
                anchor: 'start' as const
              }, {
                dx: 50,
                dy: 5,
                anchor: 'start' as const
              }, {
                dx: -50,
                dy: 5,
                anchor: 'end' as const
              }, {
                dx: -50,
                dy: 5,
                anchor: 'end' as const
              }];
              const offset = labelOffsets[i];
              return <g key={i} data-id="element-185">
                    <circle cx={pos.cx} cy={pos.cy} r="34" fill="#B8976A" stroke="#A68558" strokeWidth="2" data-id="element-186" />
                    <text x={pos.cx} y={pos.cy + 8} textAnchor="middle" fill="white" fontSize="24" fontWeight="700" fontFamily="Montserrat, sans-serif" data-id="element-187">
                      {step.letter}
                    </text>

                    {labelLines.map((line, li) => <text key={li} x={pos.cx + offset.dx} y={pos.cy + offset.dy + li * 17} textAnchor={offset.anchor} fill="#1F3A5F" fontSize="12.5" fontWeight="600" fontFamily="Montserrat, sans-serif" data-id="element-188">
                        {line}
                      </text>)}
                  </g>;
            })}
            </svg>
          </motion.div>

          {/* Tablet: Show diagram below text */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="hidden md:block lg:hidden relative w-full max-w-[520px] mx-auto" data-id="element-189">
            <svg viewBox="0 0 700 500" className="w-full h-auto" overflow="visible" data-id="element-190">
              <defs data-id="element-191">
                <marker id="arrowhead-tablet" markerWidth="10" markerHeight="8" refX="8" refY="4" orient="auto" data-id="element-192">
                  <path d="M 0 0 L 10 4 L 0 8 Z" fill="#1F3A5F" data-id="element-193" />
                </marker>
              </defs>

              {arrowPaths.map((d, i) => <path key={i} d={d} fill="none" stroke="#1F3A5F" strokeWidth="3" strokeLinecap="round" markerEnd="url(#arrowhead-tablet)" data-id="element-194" />)}

              <text x="350" y="245" textAnchor="middle" fill="#1F3A5F" fontSize="28" fontWeight="700" letterSpacing="-0.5" fontFamily="Montserrat, sans-serif" data-id="element-195">
                L.E.A.D.S.™
              </text>
              <text x="350" y="278" textAnchor="middle" fill="#1F3A5F" fontSize="19" fontWeight="700" fontFamily="Montserrat, sans-serif" data-id="element-196">
                Coaching Arch
              </text>

              {circlePositions.map((pos, i) => {
              const step = steps[i];
              const labelLines = step.title.split('\n');
              const labelOffsets = [{
                dx: 0,
                dy: -48,
                anchor: 'middle' as const
              }, {
                dx: 50,
                dy: 5,
                anchor: 'start' as const
              }, {
                dx: 50,
                dy: 5,
                anchor: 'start' as const
              }, {
                dx: -50,
                dy: 5,
                anchor: 'end' as const
              }, {
                dx: -50,
                dy: 5,
                anchor: 'end' as const
              }];
              const offset = labelOffsets[i];
              return <g key={i} data-id="element-197">
                    <circle cx={pos.cx} cy={pos.cy} r="34" fill="#B8976A" stroke="#A68558" strokeWidth="2" data-id="element-198" />
                    <text x={pos.cx} y={pos.cy + 8} textAnchor="middle" fill="white" fontSize="24" fontWeight="700" fontFamily="Montserrat, sans-serif" data-id="element-199">
                      {step.letter}
                    </text>
                    {labelLines.map((line, li) => <text key={li} x={pos.cx + offset.dx} y={pos.cy + offset.dy + li * 17} textAnchor={offset.anchor} fill="#1F3A5F" fontSize="12.5" fontWeight="600" fontFamily="Montserrat, sans-serif" data-id="element-200">
                        {line}
                      </text>)}
                  </g>;
            })}
            </svg>
          </motion.div>
        </div>

        {/* Mobile: Vertical step layout */}
        <div className="md:hidden mb-16" data-id="element-201">
          {steps.map((step, index) => <motion.div key={index} initial={{
          opacity: 0,
          x: -16
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-30px'
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="flex items-start gap-5 mb-8 last:mb-0" data-id="element-202">
              <div className="flex flex-col items-center flex-shrink-0" data-id="element-203">
                <div className="w-14 h-14 bg-gold rounded-full flex items-center justify-center shadow-lg shadow-gold/20" data-id="element-204">
                  <span className="font-heading font-bold text-lg text-white" data-id="element-205">
                    {step.letter}
                  </span>
                </div>
                {index < steps.length - 1 && <div className="w-[2px] h-8 bg-navy/20 mt-2" data-id="element-206" />}
              </div>
              <div className="pt-1" data-id="element-207">
                <h3 className="text-[17px] font-heading font-bold text-navy mb-1 leading-snug" data-id="element-208">
                  {step.title.replace('\n', ' ')}
                </h3>
                <p className="text-[14px] leading-[1.65] text-charcoal" data-id="element-209">
                  {step.text}
                </p>
              </div>
            </motion.div>)}
        </div>

        {/* Steps Description Grid - Desktop/Tablet */}
        <div className="hidden md:grid grid-cols-5 gap-4 lg:gap-5" data-id="element-210">
          {steps.map((step, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: index * 0.12
        }} className="group relative overflow-hidden bg-white rounded-xl border border-warm-border p-6 lg:p-7 text-left hover:shadow-lg hover:shadow-navy/5 hover:-translate-y-1 transition-all duration-300" data-id="element-211">
              {/* Large watermark letter */}
              <span className="absolute -top-2 -right-1 text-[120px] lg:text-[140px] font-heading font-black text-navy/[0.04] leading-none select-none pointer-events-none group-hover:text-navy/[0.07] transition-colors duration-300" data-id="element-212">
                {step.letter}
              </span>

              {/* Gold top accent line */}
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-gold to-gold-light" data-id="element-213" />

              {/* Step number badge */}
              <div className="relative flex items-center gap-3 mb-4" data-id="element-214">
                <div className="w-9 h-9 bg-navy rounded-lg flex items-center justify-center shadow-sm" data-id="element-215">
                  <span className="font-heading font-bold text-[13px] text-gold" data-id="element-216">
                    {step.letter}
                  </span>
                </div>
                <div className="h-[1px] flex-1 bg-warm-border" data-id="element-217" />
              </div>

              {/* Content */}
              <h3 className="relative text-[15px] lg:text-[16px] font-heading font-bold text-navy mb-2.5 leading-snug" data-id="element-218">
                {step.title.replace('\n', ' ')}
              </h3>
              <p className="relative text-[13px] lg:text-[14px] leading-[1.7] text-charcoal/80" data-id="element-219">
                {step.text}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}